import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Trash2, Calendar, ArrowUpDown, User, FileText, Search, X, ChevronDown } from 'lucide-react';
import EmptyState from './EmptyState';
import { Button } from './ui/button';
import { toast } from 'sonner';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import PostponementWizard from './PostponementWizard';
import DatePickerWithInput from './DatePickerWithInput';

import DatePicker, { registerLocale } from 'react-datepicker';
import el from 'date-fns/locale/el';
import 'react-datepicker/dist/react-datepicker.css';

registerLocale('el', el);

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to capitalize only first letter of first word (keep accents)
export const capitalizeFirst = (str) => {
  if (!str) return '';
  const s = str.trim();
  let result;
  if (s === s.toUpperCase()) {
    result = s.toLowerCase();
  } else {
    // Μικτή γραφή: διατήρησε λέξεις γραμμένες ΟΛΟΚΕΦΑΛΑΙΑ (ονόματα πελατών, ακρωνύμια)
    result = s.split(/(\s+)/).map((w) => {
      const letters = w.replace(/[^\p{L}]/gu, '');
      if (letters.length >= 2 && letters === letters.toUpperCase()) return w;
      return w.toLowerCase();
    }).join('');
  }
  result = result.charAt(0).toUpperCase() + result.slice(1);
  // Preserve capitalization for known proper nouns (Greek-aware: \b doesn't work with Greek)
  const properNouns = ['Κτηματολόγιο', 'Άρειος Πάγος', 'ΣτΕ', 'Ελλάδα'];
  properNouns.forEach((noun) => {
    const re = new RegExp(noun, 'gi');
    result = result.replace(re, noun);
  });
  return result;
};

// Status options for deadline entries (ΕΚΠΡΟΘΕΣΜΗ is automatic, not in dropdown)
const STATUS_OPTIONS = [
  { value: 'ΣΕ ΑΝΑΜΟΝΗ', label: 'Σε αναμονή' },
  { value: 'ΟΛΟΚΛΗΡΩΜΕΝΗ', label: 'Ολοκληρωμένη' },
  { value: 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ', label: 'Προγραμματισμός' },
];

// Status options for ΔΙΚΑΣΙΜΟΣ entries
const HEARING_STATUS_OPTIONS = [
  { value: 'ΣΕ ΑΝΑΜΟΝΗ', label: 'Σε αναμονή' },
  { value: 'ΣΥΖΗΤΗΘΗΚΕ', label: 'Συζητήθηκε' },
  { value: 'ΑΝΑΒΟΛΗ', label: 'Αναβολή' },
  { value: 'ΜΑΤΑΙΩΣΗ', label: 'Ματαίωση' },
  { value: 'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ', label: 'Παραίτηση' },
];

// Case description types mapping
export const CASE_DESCRIPTION_LABELS = {
  'debt': 'Αγωγή χρέους',
  'property': 'Εμπράγματη αγωγή',
  'fraud': 'Αγωγή καταδολίευσης',
  'inheritance': 'Κληρονομική διαφορά',
  'divorce': 'Διαζύγιο',
  'alimony': 'Διατροφή',
  'custody': 'Επιμέλεια τέκνων',
  'lease': 'Μισθωτική διαφορά',
  'labor': 'Εργατική διαφορά',
  'tort': 'Αδικοπραξία',
  'contract': 'Συμβατική διαφορά',
  'unjust_enrichment': 'Αδικαιολόγητος πλουτισμός',
  'compensation': 'Αποζημίωση',
  'ownership': 'Αναγνώριση κυριότητας',
  'possession': 'Προστασία νομής',
  'easement': 'Δουλεία',
  'condominium': 'Διαφορά οροφοκτησίας',
  'insurance': 'Ασφαλιστική διαφορά',
  'medical': 'Ιατρική αμέλεια',
  'traffic': 'Τροχαίο ατύχημα',
  'other': 'Άλλο'
};

// Court type labels
export const COURT_TYPE_LABELS = {
  'single_peace': 'Μονομελές Ειρηνοδικείο',
  'multi_peace': 'Πολυμελές Ειρηνοδικείο',
  'single_first': 'Μονομελές Πρωτοδικείο',
  'multi_first': 'Πολυμελές Πρωτοδικείο',
  'single_appeal': 'Μονομελές Εφετείο',
  'multi_appeal': 'Τριμελές Εφετείο',
  'triple_appeal': 'Τριμελές Εφετείο',
  'supreme': 'Άρειος Πάγος',
  'administrative_first': 'Διοικητικό Πρωτοδικείο',
  'single_admin_first': 'Μονομελές Διοικητικό Πρωτοδικείο',
  'triple_admin_first': 'Τριμελές Διοικητικό Πρωτοδικείο',
  'administrative_appeal': 'Διοικητικό Εφετείο',
  'admin_appeal': 'Διοικητικό Εφετείο',
  'triple_admin_appeal': 'Τριμελές Διοικητικό Εφετείο',
  'council_of_state': 'Συμβούλιο της Επικρατείας',
  'single_criminal': 'Μονομελές Πλημμελειοδικείο',
  'three_criminal': 'Τριμελές Πλημμελειοδικείο',
  'single_misdemeanor': 'Μονομελές Πλημμελειοδικείο',
  'triple_misdemeanor': 'Τριμελές Πλημμελειοδικείο',
  'single_criminal_appeal': 'Μονομελές Εφετείο Κακουργημάτων',
  'triple_criminal_appeal': 'Τριμελές Εφετείο Κακουργημάτων',
  'five_criminal_appeal': 'Πενταμελές Εφετείο',
  'mixed_court': 'Μικτό Ορκωτό Δικαστήριο',
  'mixed_jury': 'Μικτό Ορκωτό',
  'appeal_criminal': 'Εφετείο Κακουργημάτων',
  'supreme_criminal': 'Άρειος Πάγος (Ποινικό)'
};

// Document type labels
export const DOCUMENT_TYPE_LABELS = {
  'lawsuit': 'Αγωγή',
  'special_lawsuit': 'Αγωγή (Ειδική Διαδικασία)',
  'voluntary_app': 'Αίτηση Εκουσίας',
  'interim_app': 'Αίτηση ασφαλιστικών μέτρων',
  'appeal_peace': 'Έφεση Ειρηνοδικείου',
  'appeal_single_first': 'Έφεση Μονομελούς Πρωτοδικείου',
  'appeal': 'Έφεση',
  'cassation': 'Αίτηση αναίρεσης',
  'cassation_admin': 'Αίτηση αναίρεσης (ΣτΕ)',
  'injunction': 'Διαταγή Πληρωμής',
  'objection': 'Ανακοπή',
  'objection_dp': 'Ανακοπή κατά Διαταγής Πληρωμής',
  'objection_933': 'Ανακοπή 933',
  'objection_default': 'Ανακοπή ερημοδικίας',
  'appeal_lawsuit': 'Αγωγή (Διοικητικό)',
  'appeal_admin': 'Προσφυγή',
  'admin_prosfygi': 'Προσφυγή',
  'admin_agogi': 'Αγωγή (Διοικητικό)',
  'admin_efesi': 'Έφεση (Διοικητικό)',
  'cancellation_request': 'Αίτηση ακύρωσης',
  'criminal_appeal': 'Έφεση (Ποινική)',
  'criminal_cassation': 'Αναίρεση (Ποινική)',
  'revision': 'Αναψηλάφηση',
  'third_party_obj': 'Τριτανακοπή',
  'rent_objection': 'Ανακοπή κατά διαταγής απόδοσης μισθίου',
  'objection_954': 'Ανακοπή άρθρου 954 ΚΠολΔ',
  'small_claims': 'Αγωγή μικροδιαφορών'
};

const EntriesList = ({ entries, allEntries, clients, cases, onDelete, selectedDate, onClearFilter, onChangeStatsFilter, onEntryClick, statsFilter, onRefresh, matchCalendarHeight, onOpenNewEntry }) => {
  const [deleteId, setDeleteId] = useState(null);
  const [sortOrder, setSortOrder] = useState('asc'); // asc = παλιά πρώτα (χρονολογική σειρά)
  const [searchQuery, setSearchQuery] = useState('');
  const [updatingStatus, setUpdatingStatus] = useState(null); // Track which entry is being updated
  const [openStatusDropdown, setOpenStatusDropdown] = useState(null); // Track which dropdown is open
  const [showScheduleDatePicker, setShowScheduleDatePicker] = useState(null); // Track which entry shows date picker
  const [scheduledDateTemp, setScheduledDateTemp] = useState(null); // Temporary scheduled date
  const [showPostponementPicker, setShowPostponementPicker] = useState(null); // Track which entry shows postponement picker (for regular deadlines)
  const [showPostponementWizard, setShowPostponementWizard] = useState(false); // Show postponement wizard
  const [postponementEntry, setPostponementEntry] = useState(null); // Entry for postponement wizard
  const datePickerRef = useRef(null);

  // Auto-scroll to show full date picker when it opens
  useEffect(() => {
    if (showScheduleDatePicker && datePickerRef.current) {
      const timer = setTimeout(() => {
        if (datePickerRef.current) {
          datePickerRef.current.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center'
          });
        }
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showScheduleDatePicker]);

  // Close date picker when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (datePickerRef.current && !datePickerRef.current.contains(event.target)) {
        setShowScheduleDatePicker(null);
        setScheduledDateTemp(null);
      }
    };

    if (showScheduleDatePicker) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showScheduleDatePicker]);

  // Function to update entry status
  const handleStatusChange = async (entryId, newStatus, scheduledDate = null) => {
    // If ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ is selected, show date picker first
    if (newStatus === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && !scheduledDate) {
      setShowScheduleDatePicker(entryId);
      setOpenStatusDropdown(null);
      return;
    }
    
    // If ΑΝΑΒΟΛΗ is selected for hearing entry, show postponement wizard
    if (newStatus === 'ΑΝΑΒΟΛΗ') {
      const entry = entries.find(e => e.id === entryId);
      if (entry && isHearingEntry(entry)) {
        setPostponementEntry(entry);
        setShowPostponementWizard(true);
        setOpenStatusDropdown(null);
        return;
      }
    }
    
    setUpdatingStatus(entryId);
    try {
      const payload = { status: newStatus };
      if (newStatus === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && scheduledDate) {
        payload.scheduled_date = scheduledDate;
      }
      await axios.patch(`${API}/entries/${entryId}/status`, payload);
      toast.success('Η κατάσταση ενημερώθηκε');
      setOpenStatusDropdown(null);
      setShowScheduleDatePicker(null);
      setScheduledDateTemp(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Σφάλμα κατά την ενημέρωση κατάστασης');
    } finally {
      setUpdatingStatus(null);
    }
  };
  
  // Handle postponement date selection for hearings
  const handlePostponementDateSelect = async (entryId, date) => {
    if (!date) return;
    
    const entry = entries.find(e => e.id === entryId);
    if (!entry) return;
    
    // Format date correctly without timezone issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const formattedDate = `${year}-${month}-${day}`;
    
    setUpdatingStatus(entryId);
    try {
      // Use the new postponement endpoint that handles everything
      const response = await axios.post(`${API}/entries/${entryId}/postpone`, {
        new_date: formattedDate,
        recalculate_deadlines: true
      });
      
      toast.success(response.data.message || 'Η αναβολή καταχωρήθηκε επιτυχώς');
      setShowPostponementPicker(null);
      setOpenStatusDropdown(null);
      if (onRefresh) {
        onRefresh();
      }
    } catch (error) {
      console.error('Error handling postponement:', error);
      toast.error('Σφάλμα κατά την καταχώρηση αναβολής');
    } finally {
      setUpdatingStatus(null);
    }
  };

  // Handle scheduled date selection
  const handleScheduledDateSelect = (entryId, date) => {
    if (date) {
      const formattedDate = date.toISOString().split('T')[0];
      handleStatusChange(entryId, 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ', formattedDate);
    }
  };

  // Check if entry is a ΔΙΚΑΣΙΜΟΣ entry
  // Check if entry is a ΔΙΚΑΣΙΜΟΣ entry (handles accents and entry_type)
  const isHearingEntry = (entry) => {
    // Check entry_type first
    if (entry.entry_type === 'hearing') return true;
    
    if (!entry.title) return false;
    // Normalize: uppercase and remove Greek accents
    const normalized = entry.title.toUpperCase()
      .replace(/Ά/g, 'Α').replace(/Έ/g, 'Ε').replace(/Ή/g, 'Η')
      .replace(/Ί/g, 'Ι').replace(/Ό/g, 'Ο').replace(/Ύ/g, 'Υ').replace(/Ώ/g, 'Ω');
    return normalized === 'ΔΙΚΑΣΙΜΟΣ';
  };

  // Get status options based on entry type
  const getStatusOptions = (entry) => {
    return isHearingEntry(entry) ? HEARING_STATUS_OPTIONS : STATUS_OPTIONS;
  };

  // Get display status - considers if deadline has passed
  const getDisplayStatus = (entry) => {
    // Status labels mapping (lowercase display)
    const statusLabels = {
      'ΣΕ ΑΝΑΜΟΝΗ': 'Σε αναμονή',
      'ΟΛΟΚΛΗΡΩΜΕΝΗ': 'Ολοκληρωμένη',
      'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ': 'Προγραμματισμός',
      'ΑΝΑΒΟΛΗ': 'Αναβολή',
      'ΜΑΤΑΙΩΣΗ': 'Ματαίωση',
      'ΠΑΡΑΙΤΗΣΗ ΑΠΟ ΔΙΚΟΓΡΑΦΟ': 'Παραίτηση'
    };
    
    // For ΔΙΚΑΣΙΜΟΣ entries, use hearing status options
    if (isHearingEntry(entry)) {
      const hearingStatuses = HEARING_STATUS_OPTIONS.map(o => o.value);
      if (entry.status && hearingStatuses.includes(entry.status)) {
        return statusLabels[entry.status] || entry.status;
      }
      return 'Σε αναμονή';
    }
    
    const entryDate = new Date(entry.date);
    const today = new Date();
    entryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    // If deadline has passed and status is not ΟΛΟΚΛΗΡΩΜΕΝΗ, show Εκπρόθεσμη
    if (entryDate < today && entry.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ') {
      return 'Εκπρόθεσμη';
    }
    
    // If status is ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ and has scheduled_date, show with date
    if (entry.status === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && entry.scheduled_date) {
      const schedDate = new Date(entry.scheduled_date);
      const dd = String(schedDate.getDate()).padStart(2, '0');
      const mm = String(schedDate.getMonth() + 1).padStart(2, '0');
      const yyyy = schedDate.getFullYear();
      return `Για ${dd}/${mm}/${yyyy}`;
    }
    
    // Map old status values to new ones
    const validStatuses = ['ΣΕ ΑΝΑΜΟΝΗ', 'ΟΛΟΚΛΗΡΩΜΕΝΗ', 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ'];
    if (entry.status && validStatuses.includes(entry.status)) {
      return statusLabels[entry.status] || entry.status;
    }
    
    // Default to Σε αναμονή for old/unknown status values
    return 'Σε αναμονή';
  };

  // Check if entry is expired (for disabling dropdown)
  // ΔΙΚΑΣΙΜΟΣ entries are never auto-expired - they have their own status flow
  const isExpired = (entry) => {
    if (isHearingEntry(entry)) {
      return false; // ΔΙΚΑΣΙΜΟΣ entries always allow status change
    }
    const entryDate = new Date(entry.date);
    const today = new Date();
    entryDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    return entryDate < today && entry.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ';
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const fullDate = date.toLocaleDateString('el-GR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    });
    return fullDate;
  };

  const getDaysRemaining = (dateString) => {
    const targetDate = new Date(dateString);
    const today = new Date();
    
    // Set both dates to midnight for accurate day calculation
    targetDate.setHours(0, 0, 0, 0);
    today.setHours(0, 0, 0, 0);
    
    const diffTime = targetDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    
    return diffDays;
  };

  const formatDaysRemaining = (days) => {
    if (days < 0) {
      const absDays = Math.abs(days);
      return `Έληξε πριν ${absDays} ${absDays === 1 ? 'ημέρα' : 'ημέρες'}`;
    } else if (days === 0) {
      return 'Λήγει σήμερα';
    } else if (days === 1) {
      return 'Απομένει 1 ημέρα';
    } else {
      return `Απομένουν ${days} ημέρες`;
    }
  };

  // Helper to remove Greek accents from uppercase text
  const removeAccents = (str) => {
    if (!str) return '';
    const accentMap = {
      'Ά': 'Α', 'Έ': 'Ε', 'Ή': 'Η', 'Ί': 'Ι', 'Ό': 'Ο', 'Ύ': 'Υ', 'Ώ': 'Ω',
      'ά': 'Α', 'έ': 'Ε', 'ή': 'Η', 'ί': 'Ι', 'ό': 'Ο', 'ύ': 'Υ', 'ώ': 'Ω',
      'ϊ': 'Ι', 'ϋ': 'Υ', 'ΐ': 'Ι', 'ΰ': 'Υ', 'Ϊ': 'Ι', 'Ϋ': 'Υ'
    };
    return str.toUpperCase().split('').map(char => accentMap[char] || char).join('');
  };

  const getClientName = (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return 'ΑΓΝΩΣΤΟΣ';
    let name;
    if (client.client_type === 'natural') {
      const surname = (client.surname || '').trim();
      const firstName = (client.name || '').trim();
      const parts = [surname, firstName].filter(Boolean);
      name = parts.join(' ') || 'ΑΓΝΩΣΤΟΣ';
    } else {
      name = client.company_name || 'ΑΓΝΩΣΤΟΣ';
    }
    return removeAccents(name).toUpperCase();
  };

  // Get case description label from type
  const getCaseDescriptionLabel = (entry) => {
    if (entry.case_description_type === 'other' && entry.case_description_custom) {
      return entry.case_description_custom;
    }
    if (entry.case_description_type) {
      return CASE_DESCRIPTION_LABELS[entry.case_description_type] || entry.case_description_type;
    }
    return null;
  };

  // Build a compact "/" separated summary of case data for an entry
  const getCaseSummary = (entry) => {
    const caseObj = (cases || []).find(c => c.id === entry.case_id);
    const courtType = caseObj?.court_type || entry.court_type;
    const courtLocation = caseObj?.court_location || entry.court_location;
    const documentType = caseObj?.document_type || entry.document_type;
    const caseDesc = getCaseDescriptionLabel(entry);

    const parts = [
      courtType ? COURT_TYPE_LABELS[courtType] || courtType : null,
      courtLocation || null,
      documentType ? DOCUMENT_TYPE_LABELS[documentType] || documentType : null,
      caseObj?.client_role || null,
      caseDesc,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' | ') : null;
  };

  const handleDeleteClick = (id) => {
    setDeleteId(id);
  };

  const handleConfirmDelete = () => {
    if (deleteId) {
      onDelete(deleteId);
      setDeleteId(null);
    }
  };

  const toggleSortOrder = () => {
    setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
  };

  // Filter entries by selected date (includes both deadline date AND scheduled date)
  // First, hide replaced entries (ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ) except hearings and completed ones
  const visibleEntries = entries.filter(e => {
    // Ραντεβού δεν εμφανίζονται εδώ — φαίνονται στο Πρόγραμμα ημέρας
    const normTitle = (e.title || '').toUpperCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    if (e.entry_type === 'other_activity' && normTitle.includes('ΡΑΝΤΕΒΟΥ')) return false;
    // Δικάσιμοι δεν εμφανίζονται εδώ — φαίνονται στο Πρόγραμμα ημέρας
    if (e.entry_type === 'hearing' || normTitle.includes('ΔΙΚΑΣΙΜ')) return false;
    // Exception 2: Always show completed entries
    if (e.status === 'ΟΛΟΚΛΗΡΩΜΕΝΗ') return true;
    // Hide replaced entries
    if (e.status === 'ΑΝΤΙΚΑΤΑΣΤΑΘΗΚΕ') return false;
    return true;
  });

  let filteredEntries = selectedDate 
    ? visibleEntries.filter(entry => {
        // Check deadline date
        const entryDate = new Date(entry.date);
        const matchesDeadline = (
          entryDate.getDate() === selectedDate.getDate() &&
          entryDate.getMonth() === selectedDate.getMonth() &&
          entryDate.getFullYear() === selectedDate.getFullYear()
        );
        
        // Check scheduled date (for ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ entries)
        let matchesScheduled = false;
        if (entry.status === 'ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ' && entry.scheduled_date) {
          const schedDate = new Date(entry.scheduled_date);
          matchesScheduled = (
            schedDate.getDate() === selectedDate.getDate() &&
            schedDate.getMonth() === selectedDate.getMonth() &&
            schedDate.getFullYear() === selectedDate.getFullYear()
          );
        }
        
        return matchesDeadline || matchesScheduled;
      })
    : visibleEntries;

  // Filter by search query
  if (searchQuery.trim()) {
    filteredEntries = filteredEntries.filter(entry => {
      const query = searchQuery.toLowerCase();
      
      // Βρες τον πελάτη για αυτή την εγγραφή
      const client = clients.find(c => c.client_id === entry.client_id);
      let clientName = '';
      if (client) {
        if (client.client_type === 'natural') {
          clientName = `${client.first_name || ''} ${client.last_name || ''}`.toLowerCase();
        } else {
          clientName = (client.company_name || '').toLowerCase();
        }
      }
      
      return (
        entry.title?.toLowerCase().includes(query) ||
        entry.date?.toLowerCase().includes(query) ||
        clientName.includes(query)
      );
    });
  }

  const sortedEntries = [...filteredEntries].sort((a, b) => {
    const dateA = new Date(a.date);
    const dateB = new Date(b.date);
    return sortOrder === 'asc' ? dateA - dateB : dateB - dateA;
  });

  const getPriorityColor = (priority) => {
    const colors = {
      high: 'text-rose-700 bg-rose-50 border-rose-300',
      medium: 'text-slate-700 bg-stone-100 border-stone-300',
      low: 'text-slate-600 bg-stone-50 border-stone-200',
    };
    return colors[priority] || colors.medium;
  };

  const getPriorityLabel = (priority) => {
    const labels = {
      high: 'Υψηλή',
      medium: 'Μεσαία',
      low: 'Χαμηλή',
    };
    return labels[priority] || labels.medium;
  };

  const isPast = (dateString) => {
    return new Date(dateString) < new Date();
  };

  // Match the exact rendered height of the monthly calendar (set via --cal-h by Dashboard)
  const heightClass = matchCalendarHeight 
    ? 'cp-match-cal-height' 
    : 'lg:h-full lg:min-h-0';

  return (
    <div className={`bg-white border border-stone-200/60 rounded-2xl shadow-[0_1px_2px_rgba(15,23,42,0.04)] flex flex-col cp-mobile-fill-toolbar ${heightClass}`} data-testid="entries-list">
      <div className="p-4 max-sm:!px-4 max-sm:!pt-2 max-sm:!pb-2 border-b border-stone-100 max-sm:!border-b-0 flex-shrink-0 bg-white rounded-t-2xl cp-panel-sticky-header">
        <div className="flex items-center justify-between max-sm:hidden">
          <div>
            <h3 className="text-base sm:text-lg font-semibold text-slate-900 flex items-center gap-1.5 sm:gap-2">
              <FileText className="hidden sm:block w-4 h-4 sm:w-5 sm:h-5 text-slate-500" />
              Προθεσμίες
            </h3>
            <p className="hidden sm:block text-sm text-slate-500">Επιλέξτε προθεσμία για επεξεργασία</p>
          </div>
          <Button
            data-testid="sort-entries-btn"
            variant="ghost"
            size="sm"
            onClick={toggleSortOrder}
            className="hover:bg-stone-100 text-slate-700"
          >
            <ArrowUpDown className="w-4 h-4" />
          </Button>
        </div>
        
        {(selectedDate || statsFilter) ? (
          <div className="hidden sm:flex items-center justify-between bg-stone-100 border border-stone-300 rounded-lg px-3 py-2 mt-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-700" />
              <span className="text-sm font-medium text-slate-900">
                {statsFilter === 'today' && 'Σήμερα'}
                {statsFilter === 'week' && 'Επόμενες 7 ημέρες'}
                {statsFilter === 'month' && 'Επόμενες 30 ημέρες'}
                {statsFilter === 'year' && 'Επόμενες 365 ημέρες'}
                {selectedDate && !statsFilter && selectedDate.toLocaleDateString('el-GR', { 
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric', 
                })}
              </span>
            </div>
            <button
              onClick={() => {
                // Mobile UX: clicking next cycles "today" → "week" → "month" → "year" → "today"
                if (statsFilter === 'today' && onChangeStatsFilter) {
                  onChangeStatsFilter('week');
                } else if (statsFilter === 'week' && onChangeStatsFilter) {
                  onChangeStatsFilter('month');
                } else if (statsFilter === 'month' && onChangeStatsFilter) {
                  onChangeStatsFilter('year');
                } else if (statsFilter === 'year' && onChangeStatsFilter) {
                  onChangeStatsFilter('today');
                } else {
                  onClearFilter();
                }
              }}
              className="text-xs font-semibold text-red-700 hover:text-red-800 hover:bg-red-50 rounded px-2 py-1 transition-colors whitespace-nowrap"
              title="Αλλαγή φίλτρου χρόνου"
            >
              {statsFilter === 'today' && '7 ημέρες'}
              {statsFilter === 'week' && '30 ημέρες'}
              {statsFilter === 'month' && '365 ημέρες'}
              {statsFilter === 'year' && 'Σήμερα'}
              {selectedDate && !statsFilter && (
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              )}
            </button>
          </div>
        ) : null}

        {/* Search Input - hidden on mobile */}
        <div className="relative mt-3 hidden sm:block">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            placeholder="Αναζήτηση εγγραφών..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-9 pr-9 py-2 border border-stone-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 focus:border-transparent"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-slate-600"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        {/* (legacy filter chip below removed — it's now placed ABOVE the search) */}
        {false ? (
          <div className="flex items-center justify-between bg-stone-100 border border-stone-300 rounded-lg px-3 py-2 mt-3">
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-slate-700" />
              <span className="text-sm font-medium text-slate-900">
                {statsFilter === 'today' && 'Σήμερα'}
                {statsFilter === 'week' && 'Επόμενες 7 ημέρες'}
                {statsFilter === 'month' && 'Επόμενες 30 ημέρες'}
                {statsFilter === 'year' && 'Επόμενες 365 ημέρες'}
                {selectedDate && !statsFilter && selectedDate.toLocaleDateString('el-GR', { 
                  day: '2-digit',
                  month: '2-digit',
                  year: 'numeric', 
                })}
              </span>
            </div>
            <button
              onClick={onClearFilter}
              className="text-slate-700 hover:text-slate-900 hover:bg-slate-200 rounded p-1 transition-colors"
              title="Εμφάνιση όλων"
            >
              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        ) : null}
        
        <p className="text-sm text-slate-600 mt-2">
          {(selectedDate || statsFilter) ? `${sortedEntries.length} ${sortedEntries.length === 1 ? 'προθεσμία' : 'προθεσμίες'}` : `${visibleEntries.length} συνολικά`}
        </p>
      </div>

      <div className="flex-1 min-h-0 overflow-y-auto">
        {sortedEntries.length === 0 ? (
          <div className="p-8 text-center" data-testid="no-entries-message">
            {searchQuery ? (
              <>
                <Search className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-600 text-sm font-medium">Δεν βρέθηκαν αποτελέσματα</p>
                <p className="text-slate-400 text-xs mt-1">Δοκιμάστε διαφορετική αναζήτηση</p>
              </>
            ) : (((allEntries || entries).length > 0) && (selectedDate || statsFilter)) ? (
              <div data-testid="entries-filter-empty">
                <EmptyState
                  testId="entries-empty-state"
                  icon={Calendar}
                  text={`Καμία προθεσμία ${statsFilter === 'today' ? 'σήμερα' : statsFilter === 'week' ? 'τις επόμενες 7 ημέρες' : statsFilter === 'month' ? 'τις επόμενες 30 ημέρες' : 'την ημέρα αυτή'}`}
                  hint="Δείτε τις επερχόμενες υποχρεώσεις σας"
                />
                {(() => {
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const upcoming = (allEntries || entries)
                    .filter((e) => e.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ' && e.date && new Date(e.date) >= today)
                    .sort((a, b) => new Date(a.date) - new Date(b.date))
                    .slice(0, 5);
                  if (upcoming.length === 0) return null;
                  return (
                    <div className="sm:hidden mt-4 text-left" data-testid="upcoming-mini-list">
                      <p className="text-xs font-bold text-slate-700 tracking-wide mb-2 px-1">ΕΠΕΡΧΟΜΕΝΑ</p>
                      <div className="space-y-1.5">
                        {upcoming.map((e) => (
                          <button key={e.id} onClick={() => onEntryClick && onEntryClick(e)}
                            className="w-full text-left bg-white border border-stone-200 rounded-lg px-3 py-2 flex items-center gap-2">
                            <span className="text-[11px] font-semibold text-red-700 shrink-0">
                              {new Date(e.date).toLocaleDateString('el-GR', { day: '2-digit', month: '2-digit' })}
                            </span>
                            <span className="text-xs text-slate-800 truncate">
                              {getClientName(e.client_id)} — {capitalizeFirst(e.title)}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  );
                })()}
              </div>
            ) : (
              <EmptyState
                testId="entries-empty-state"
                icon={Calendar}
                text="Δεν υπάρχουν εγγραφές ακόμα"
                hint="Δημιουργήστε μια νέα εγγραφή για να ξεκινήσετε"
                actionLabel={onOpenNewEntry ? '+ Νέα εγγραφή' : undefined}
                onAction={onOpenNewEntry}
              />
            )}
          </div>
        ) : (
          <div className="space-y-2 p-3 sm:p-4">
            {sortedEntries.map((entry) => (
              <div
                key={entry.id}
                data-testid={`entry-item-${entry.id}`}
                className="cp-fade-in py-2.5 px-3 rounded-xl border border-stone-200/60 bg-white hover:bg-stone-50 hover:border-stone-300 transition-all cursor-pointer shadow-[0_1px_2px_rgba(15,23,42,0.04)]"
                onClick={() => onEntryClick && onEntryClick(entry)}
              >
                {/* Row 1: Client name + Status on the same row (vertically centered) */}
                <div className="flex items-center justify-between gap-2 mb-1">
                  <h4
                    className="font-bold text-sm text-slate-900 truncate flex-1 min-w-0"
                    data-testid={`entry-title-${entry.id}`}
                    onClick={() => onEntryClick && onEntryClick(entry)}
                  >
                    {getClientName(entry.client_id)}
                  </h4>
                  <div className="relative flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                    <button
                      onClick={() => !isExpired(entry) && setOpenStatusDropdown(openStatusDropdown === entry.id ? null : entry.id)}
                      disabled={updatingStatus === entry.id || isExpired(entry)}
                      className={`inline-flex items-center gap-1 px-2.5 py-1 text-xs font-medium rounded-md transition-all shadow-sm ${
                        isExpired(entry)
                          ? 'bg-rose-600 text-white cursor-not-allowed'
                          : isHearingEntry(entry)
                            ? 'bg-[#D97757] text-white hover:bg-[#C15F3C]'
                            : 'bg-[#D97757] text-white hover:bg-[#C15F3C]'
                      }`}
                      data-testid={`entry-status-btn-${entry.id}`}
                    >
                      {updatingStatus === entry.id ? (
                        <span className="animate-pulse">...</span>
                      ) : (
                        <>
                          <span>{getDisplayStatus(entry)}</span>
                          {!isExpired(entry) && <ChevronDown className="w-3 h-3" />}
                        </>
                      )}
                    </button>

                    {/* Dropdown Menu */}
                    {openStatusDropdown === entry.id && !isExpired(entry) && (
                      <div className="absolute right-0 mt-1 w-52 bg-white border border-stone-200 rounded-md shadow-lg z-50">
                        {getStatusOptions(entry).map((option) => (
                          <button
                            key={option.value}
                            onClick={() => handleStatusChange(entry.id, option.value)}
                            className={`w-full text-left px-3 py-2 text-sm hover:bg-stone-100 first:rounded-t-md last:rounded-b-md ${
                              (entry.status || 'ΣΕ ΑΝΑΜΟΝΗ') === option.value ? 'bg-slate-200 font-medium text-slate-900' : 'text-slate-700'
                            }`}
                            data-testid={`status-option-${option.value}`}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
                
                {/* Date Picker for ΠΡΟΓΡΑΜΜΑΤΙΣΜΟΣ - Centered in entry cell */}
                {showScheduleDatePicker === entry.id && (
                  <div className="flex justify-center my-3" onClick={(e) => e.stopPropagation()}>
                    <div 
                      ref={datePickerRef}
                      className="bg-white border border-stone-200 rounded-lg shadow-xl p-3"
                    >
                      <DatePickerWithInput
                        selected={scheduledDateTemp}
                        onChange={(date) => handleScheduledDateSelect(entry.id, date)}
                        minDate={new Date()}
                      />
                      <button
                        onClick={() => {
                          setShowScheduleDatePicker(null);
                          setScheduledDateTemp(null);
                        }}
                        className="w-full text-xs text-slate-500 hover:text-red-600 py-1 border-t border-stone-100 mt-2"
                      >
                        Ακύρωση
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Date Picker for ΑΝΑΒΟΛΗ ΔΙΚΑΣΙΜΟΥ */}
                {showPostponementPicker === entry.id && (
                  <div className="flex justify-center my-3" onClick={(e) => e.stopPropagation()}>
                    <div className="bg-white border border-amber-300 rounded-lg shadow-xl p-3">
                      <p className="text-sm font-medium text-red-700 mb-2 text-center">Επιλέξτε νέα ημερομηνία δικασίμου</p>
                      <DatePickerWithInput
                        selected={null}
                        onChange={(date) => handlePostponementDateSelect(entry.id, date)}
                        minDate={new Date()}
                      />
                      <button
                        onClick={() => setShowPostponementPicker(null)}
                        className="w-full text-xs text-slate-500 hover:text-red-600 py-1 border-t border-stone-100 mt-2"
                      >
                        Ακύρωση
                      </button>
                    </div>
                  </div>
                )}
                
                {/* Row 3: Case Summary (Court / Location / Document / Description) — hidden for other_activity since description appears below as notes */}
                {entry.entry_type !== 'other_activity' && (() => {
                  const summary = getCaseSummary(entry);
                  if (!summary && !entry.opponent_details) return null;
                  return (
                    <div className="mb-1 space-y-0.5" data-testid={`entry-case-summary-${entry.id}`}>
                      {summary && (
                        <p className="text-xs text-slate-600">{summary}</p>
                      )}
                      {entry.opponent_details && (
                        <p className="text-xs text-slate-600">Αντίδικος: {entry.opponent_details}</p>
                      )}
                    </div>
                  );
                })()}
                
                {/* Row 2: Entry Title - Bold */}
                <p 
                  className="text-sm font-bold text-red-700 mb-1"
                  onClick={() => onEntryClick && onEntryClick(entry)}
                >
                  {capitalizeFirst(entry.title)}
                </p>
                
                {/* Notes - show if exists. Hidden for hearings (δικάσιμο) because the same info already appears in the case summary row above. */}
                {entry.notes && !isHearingEntry(entry) && (
                  <p className="text-xs text-slate-500 mt-1 whitespace-pre-wrap break-words">
                    {entry.notes}
                  </p>
                )}
                
                {/* Row 3: Date & Days Remaining + Delete */}
                <div 
                  className="mt-2 space-y-1 cursor-pointer"
                  onClick={() => onEntryClick && onEntryClick(entry)}
                >
                  <p className="text-xs text-slate-500" data-testid={`entry-date-${entry.id}`}>
                    {entry.status === 'ΣΥΖΗΤΗΘΗΚΕ' ? <span>Συζήτηση: </span> : (!isHearingEntry(entry) && entry.entry_type !== 'other_activity') ? <span>Λήξη προθεσμίας: </span> : null}
                    <span>{formatDate(entry.date)}</span>
                  </p>
                  <div className="flex items-center justify-between">
                    {entry.status !== 'ΟΛΟΚΛΗΡΩΜΕΝΗ' ? (
                    <p
                      className={`text-sm font-semibold ${
                        isPast(entry.date)
                          ? 'text-rose-600'
                          : getDaysRemaining(entry.date) < 10
                          ? 'text-red-600'
                          : 'text-[#D97757]'
                      }`}
                      data-testid={`entry-days-remaining-${entry.id}`}
                    >
                      {isPast(entry.date) && '⚠️ '}
                      {formatDaysRemaining(getDaysRemaining(entry.date))}
                    </p>
                    ) : (
                    <p className="text-sm font-semibold text-green-600" data-testid={`entry-days-remaining-${entry.id}`}>
                      Ολοκληρώθηκε
                    </p>
                    )}
                    <button
                      data-testid={`delete-entry-btn-${entry.id}`}
                      onClick={(e) => { e.stopPropagation(); handleDeleteClick(entry.id); }}
                      className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                      title="Διαγραφή εγγραφής"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )
        }
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteId} onOpenChange={() => setDeleteId(null)}>
        <AlertDialogContent data-testid="delete-confirmation-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Είστε σίγουροι;</AlertDialogTitle>
            <AlertDialogDescription>
              Αυτή η ενέργεια δεν μπορεί να αναιρεθεί. Η εγγραφή θα διαγραφεί μόνιμα.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="delete-cancel-btn">Ακύρωση</AlertDialogCancel>
            <AlertDialogAction
              data-testid="delete-confirm-btn"
              onClick={handleConfirmDelete}
              className="bg-rose-600 hover:bg-rose-700"
            >
              Διαγραφή
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Postponement Wizard for hearings */}
      {showPostponementWizard && postponementEntry && (
        <PostponementWizard
          isOpen={showPostponementWizard}
          onClose={() => {
            setShowPostponementWizard(false);
            setPostponementEntry(null);
          }}
          entry={postponementEntry}
          entries={entries}
          onRefresh={onRefresh}
        />
      )}
    </div>
  );
};

export default EntriesList;